import React, { useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  IconFileCode, IconUpload, IconEye, IconPencil, IconToggleRight, IconToggleLeft,
} from '@tabler/icons-react'
import { PageTitle } from 'context/PageTitleContext'
import {
  getSwmsTemplates,
  createSwmsTemplate,
  updateSwmsTemplate,
  activateSwmsTemplate,
  deactivateSwmsTemplate,
  getSwmsTemplateUrl,
} from 'services/swmsTemplates.svc'
import './SwmsTemplates.scss'

export default function SwmsTemplates() {
  const queryClient = useQueryClient()
  const { data: templates = [], isLoading, isError } = useQuery(['swmsTemplates'], getSwmsTemplates)

  const [modalState, setModalState] = useState({ isOpen: false, mode: 'create', template: null })
  const [templateFilter, setTemplateFilter] = useState('all')
  const [formData, setFormData] = useState({ name: '', description: '', file: null })
  const [fileError, setFileError] = useState('')
  const [isUploading, setIsUploading] = useState(false)
  const [confirmTemplate, setConfirmTemplate] = useState(null)
  const [toast, setToast] = useState({ show: false, message: '', isError: false })
  const toastTimer = useRef(null)

  const showToast = (message, isError = false) => {
    clearTimeout(toastTimer.current)
    setToast({ show: true, message, isError })
    toastTimer.current = setTimeout(() => setToast({ show: false, message: '', isError: false }), 4000)
  }

  const createMutation = useMutation(createSwmsTemplate, {
    onSuccess: () => {
      queryClient.invalidateQueries(['swmsTemplates'])
      closeModal()
      setIsUploading(false)
      showToast('Template uploaded successfully.')
    },
    onError: () => {
      showToast('Failed to create template in database.', true)
      setIsUploading(false)
    },
  })

  const updateMutation = useMutation(({ id, data }) => updateSwmsTemplate(id, data), {
    onSuccess: () => {
      queryClient.invalidateQueries(['swmsTemplates'])
      closeModal()
      showToast('Template updated.')
    },
    onError: () => showToast('Failed to update template.', true),
  })

  const activateMutation = useMutation(activateSwmsTemplate, {
    onSuccess: () => queryClient.invalidateQueries(['swmsTemplates']),
    onError: () => showToast('Failed to activate template.', true),
  })

  const deactivateMutation = useMutation(deactivateSwmsTemplate, {
    onSuccess: () => queryClient.invalidateQueries(['swmsTemplates']),
    onError: () => showToast('Failed to deactivate template.', true),
  })

  const openCreateModal = () => {
    setFormData({ name: '', description: '', file: null })
    setFileError('')
    setModalState({ isOpen: true, mode: 'create', template: null })
  }

  const openEditModal = (template) => {
    setFormData({ name: template.name, description: template.description || '', file: null })
    setFileError('')
    setModalState({ isOpen: true, mode: 'edit', template })
  }

  const closeModal = () => setModalState({ isOpen: false, mode: 'create', template: null })

  const handleFileChange = (e) => {
    const file = e.target.files[0]
    if (!file) return
    if (!file.name.toLowerCase().endsWith('.html')) {
      setFileError('Only .html files are accepted')
      setFormData((prev) => ({ ...prev, file: null }))
    } else {
      setFileError('')
      setFormData((prev) => ({ ...prev, file }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (modalState.mode === 'create') {
      setIsUploading(true)
      setFileError('')
      try {
        const htmlContent = await formData.file.text()
        createMutation.mutate({
          name: formData.name,
          description: formData.description,
          htmlContent,
        })
      } catch (err) {
        showToast('Failed to read file.', true)
        setIsUploading(false)
      }
    } else {
      updateMutation.mutate({
        id: modalState.template._id,
        data: { name: formData.name, description: formData.description },
      })
    }
  }

  const handleConfirmDeactivate = () => {
    deactivateMutation.mutate(confirmTemplate._id)
    setConfirmTemplate(null)
  }

  const handleToggleActive = (template) => {
    if (template.isActive) {
      setConfirmTemplate(template)
    } else {
      activateMutation.mutate(template._id)
    }
  }

  const handlePreview = async (template) => {
    try {
      const data = await getSwmsTemplateUrl(template._id)
      window.open(data.url, '_blank', 'noopener,noreferrer')
    } catch {
      showToast('Failed to open preview.', true)
    }
  }

  const isFormBusy = createMutation.isLoading || updateMutation.isLoading || isUploading
  const isCreateDisabled = !formData.file || Boolean(fileError) || !formData.name || isFormBusy
  const filteredTemplates = templates.filter((template) => {
    if (templateFilter === 'active') return template.isActive
    if (templateFilter === 'inactive') return !template.isActive
    return true
  })

  const getUploaderName = (template) => {
    const firstName = template.createdByDetails?.firstName?.trim()
    const surname = template.createdByDetails?.surname?.trim()
    const fullName = [firstName, surname].filter(Boolean).join(' ')
    return fullName || template.createdByDetails?.contactEmail || '—'
  }

  const formatUploadDate = (value) => (value ? new Date(value).toLocaleString() : '—')

  return (
    <div className="swms-tpl-root">
      <PageTitle title="SWMS Templates" />

      {/* ── Heading ─────────────────────────────────────────────────────── */}
      <div className="swms-tpl-heading">
        <div className="swms-tpl-heading__title">
          <IconFileCode size={20} stroke={1.5} />
          SWMS Templates
        </div>
        <button className="swms-tpl-btn swms-tpl-btn--primary" onClick={openCreateModal}>
          <IconUpload size={14} stroke={2} />
          Upload Template
        </button>
      </div>

      {/* ── Table ───────────────────────────────────────────────────────── */}
      <div className="swms-tpl-body">
        {!isLoading && !isError && (
          <div className="swms-tpl-filters" role="tablist" aria-label="Template status filter">
            <button
              type="button"
              className={`swms-tpl-filter${templateFilter === 'all' ? ' swms-tpl-filter--active' : ''}`}
              onClick={() => setTemplateFilter('all')}
            >
              All
            </button>
            <button
              type="button"
              className={`swms-tpl-filter${templateFilter === 'active' ? ' swms-tpl-filter--active' : ''}`}
              onClick={() => setTemplateFilter('active')}
            >
              Active
            </button>
            <button
              type="button"
              className={`swms-tpl-filter${templateFilter === 'inactive' ? ' swms-tpl-filter--active' : ''}`}
              onClick={() => setTemplateFilter('inactive')}
            >
              Inactive
            </button>
          </div>
        )}
        {isLoading && (
          <div className="swms-tpl-state">
            <span className="swms-tpl-spinner" />
            Loading templates…
          </div>
        )}
        {isError && !isLoading && (
          <div className="swms-tpl-state swms-tpl-state--error">Failed to load templates.</div>
        )}
        {!isLoading && !isError && (
          <div className="swms-tpl-table-wrap">
            <table className="swms-tpl-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Uploaded</th>
                  <th>Uploaded By</th>
                  <th>Status</th>
                  <th className="swms-tpl-table__actions-col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTemplates.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="swms-tpl-empty">No templates found.</td>
                  </tr>
                ) : filteredTemplates.map((tpl) => (
                  <tr key={tpl._id}>
                    <td className="swms-tpl-table__name">{tpl.name}</td>
                    <td className="swms-tpl-table__desc">{tpl.description || '—'}</td>
                    <td>{formatUploadDate(tpl.createdAt)}</td>
                    <td>{getUploaderName(tpl)}</td>
                    <td>
                      <span className={`swms-tpl-badge${tpl.isActive ? ' swms-tpl-badge--active' : ' swms-tpl-badge--inactive'}`}>
                        {tpl.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="swms-tpl-table__actions">
                      <button
                        className="swms-tpl-act-btn"
                        onClick={() => handlePreview(tpl)}
                        aria-label="View template"
                      >
                        <IconEye size={13} stroke={1.5} />
                        View
                      </button>
                      <button
                        className="swms-tpl-act-btn"
                        onClick={() => openEditModal(tpl)}
                        aria-label="Edit template"
                      >
                        <IconPencil size={13} stroke={1.5} />
                        Edit
                      </button>
                      <button
                        className={`swms-tpl-act-btn${tpl.isActive ? ' swms-tpl-act-btn--warn' : ' swms-tpl-act-btn--ok'}`}
                        onClick={() => handleToggleActive(tpl)}
                        aria-label={tpl.isActive ? 'Deactivate template' : 'Activate template'}
                      >
                        {tpl.isActive
                          ? <><IconToggleLeft size={13} stroke={1.5} />Deactivate</>
                          : <><IconToggleRight size={13} stroke={1.5} />Activate</>}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Upload / Edit modal ──────────────────────────────────────────── */}
      {modalState.isOpen && (
        <div className="swms-tpl-overlay" role="dialog" aria-modal="true">
          <div className="swms-tpl-modal">
            <div className="swms-tpl-modal__header">
              <span>{modalState.mode === 'create' ? 'Upload Template' : 'Edit Template'}</span>
              <button className="swms-tpl-modal__close" onClick={closeModal} aria-label="Close modal">×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="swms-tpl-modal__body">
                <div className="swms-tpl-field">
                  <label className="swms-tpl-field__label" htmlFor="tpl-name">Name <span className="swms-tpl-field__req">*</span></label>
                  <input
                    id="tpl-name"
                    className="swms-tpl-field__input"
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                  />
                </div>
                <div className="swms-tpl-field">
                  <label className="swms-tpl-field__label" htmlFor="tpl-desc">Description</label>
                  <textarea
                    id="tpl-desc"
                    className="swms-tpl-field__input swms-tpl-field__input--textarea"
                    rows={3}
                    value={formData.description}
                    onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                  />
                </div>
                {modalState.mode === 'create' && (
                  <div className="swms-tpl-field">
                    <label className="swms-tpl-field__label">HTML file <span className="swms-tpl-field__req">*</span></label>
                    <label className="swms-tpl-file-btn">
                      Choose .html file
                      <input type="file" accept=".html" className="swms-tpl-file-input" onChange={handleFileChange} />
                    </label>
                    {formData.file && (
                      <span className="swms-tpl-field__hint">{formData.file.name}</span>
                    )}
                    {fileError && (
                      <span className="swms-tpl-field__error">{fileError}</span>
                    )}
                  </div>
                )}
              </div>
              <div className="swms-tpl-modal__footer">
                <button type="button" className="swms-tpl-btn swms-tpl-btn--ghost" onClick={closeModal}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="swms-tpl-btn swms-tpl-btn--primary"
                  disabled={modalState.mode === 'create' ? isCreateDisabled : (!formData.name || isFormBusy)}
                >
                  {isUploading ? 'Uploading…' : modalState.mode === 'create' ? 'Upload' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Deactivate confirm modal ─────────────────────────────────────── */}
      {confirmTemplate && (
        <div className="swms-tpl-overlay" role="dialog" aria-modal="true">
          <div className="swms-tpl-confirm">
            <div className="swms-tpl-confirm__icon swms-tpl-confirm__icon--warn">⚠</div>
            <p className="swms-tpl-confirm__title">Deactivate Template</p>
            <p className="swms-tpl-confirm__body">
              Are you sure you want to deactivate <strong>{confirmTemplate.name}</strong>?{' '}
              Company admins will no longer see it in their SWMS store.
            </p>
            <div className="swms-tpl-confirm__actions">
              <button className="swms-tpl-btn swms-tpl-btn--ghost" onClick={() => setConfirmTemplate(null)}>
                Cancel
              </button>
              <button className="swms-tpl-btn swms-tpl-btn--warn" onClick={handleConfirmDeactivate}>
                Deactivate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Toast ───────────────────────────────────────────────────────── */}
      {toast.show && (
        <div className={`swms-tpl-toast${toast.isError ? ' swms-tpl-toast--error' : ''}`}>
          {toast.message}
        </div>
      )}
    </div>
  )
}
